import React, { useState, useEffect, useCallback, useRef } from 'react';
import { showToast } from '../utils/toast';
import { fmtFecha, fmtCant } from '../utils/format';
import {
  FiPlus, FiEdit2, FiToggleLeft, FiToggleRight,
  FiSearch, FiPackage, FiAlertTriangle, FiEdit3,
  FiChevronRight, FiArchive, FiList, FiArrowDownCircle, FiArrowUpCircle, FiX,
} from 'react-icons/fi';
import api from '../services/api';
import '../styles/Almacenes.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const fmtStock = (n) =>
  parseFloat(n) % 1 === 0 ? parseInt(n).toString() : parseFloat(n).toFixed(2);

/* ── Modal Almacén ───────────────────────────────────────────── */
function ModalAlmacen({ modal, onClose, onSaved }) {
  const [form,  setForm]  = useState({ nombre: '', descripcion: '' });
  const [err,   setErr]   = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (modal?.data) setForm({ nombre: modal.data.nombre, descripcion: modal.data.descripcion || '' });
    else             setForm({ nombre: '', descripcion: '' });
    setErr('');
  }, [modal]);

  const save = async () => {
    if (!form.nombre.trim()) { setErr('El nombre es requerido'); return; }
    setSaving(true); setErr('');
    try {
      if (modal.mode === 'new') await api.post('/api/almacenes', form);
      else await api.put(`/api/almacenes/${modal.data.id}`, form);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  if (!modal) return null;
  return (
    <div className="alm-modal-overlay">
      <div className="alm-modal" onClick={e => e.stopPropagation()}>
        <div className="alm-modal-header">
          <span>{modal.mode === 'new' ? 'Nuevo Almacén' : 'Editar Almacén'}</span>
          <button className="alm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="alm-modal-body">
          <div className="alm-field-row">
            <label>Nombre *</label>
            <input
              className="alm-input" value={form.nombre} autoFocus
              onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
              placeholder="Ej: ALMACÉN PRINCIPAL, TIENDA LIMA..." />
          </div>
          <div className="alm-field-row">
            <label>Descripción</label>
            <input
              className="alm-input" value={form.descripcion}
              onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
              placeholder="Descripción opcional" />
          </div>
        </div>
        {err && <div className="alm-modal-error">{err}</div>}
        <div className="alm-modal-footer">
          <button className="alm-btn-save" onClick={save} disabled={saving}>
            {saving ? 'Guardando...' : modal.mode === 'new' ? 'Guardar' : 'Modificar'}
          </button>
          <button className="alm-btn-cancel" onClick={onClose}>× Salir</button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal Ajuste Stock ──────────────────────────────────────── */
function ModalAjuste({ item, almacenId, onClose, onSaved }) {
  const [stock,  setStock]  = useState('');
  const [err,    setErr]    = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (item) { setStock(fmtStock(item.stock)); setErr(''); }
    setTimeout(() => inputRef.current?.select(), 50);
  }, [item]);

  const save = async () => {
    const val = parseFloat(stock);
    if (isNaN(val) || val < 0) { setErr('Ingrese un valor numérico válido (≥ 0)'); return; }
    setSaving(true); setErr('');
    try {
      await api.post(`/api/almacenes/${almacenId}/productos/${item.id}/ajuste`, { stock: val });
      onSaved(item.id, val);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  if (!item) return null;
  const esEdicion = parseFloat(item.stock) !== 0;
  return (
    <div className="alm-modal-overlay">
      <div className="alm-modal alm-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="alm-modal-header">
          <span>{esEdicion ? 'Editar Stock Inicial' : 'Establecer Stock Inicial'}</span>
          <button className="alm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="alm-modal-body">
          <div className="alm-ajuste-producto">
            <span className="alm-ajuste-nombre">{item.nombre}</span>
            {item.codigo && <span className="alm-ajuste-cod">{item.codigo}</span>}
          </div>
          <div className="alm-field-row alm-field-row-inline">
            <label>Cantidad ({item.unidad_minima_short || item.unidad_minima_nombre || 'UND'})</label>
            <input
              ref={inputRef}
              className="alm-input alm-input-stock"
              type="number" min="0" step="0.01"
              value={stock}
              onChange={e => setStock(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && save()}
            />
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#94a3b8', lineHeight: 1.4 }}>
            Editable mientras no existan movimientos, ventas ni transferencias registradas.
          </p>
        </div>
        {err && <div className="alm-modal-error">{err}</div>}
        <div className="alm-modal-footer">
          <button className="alm-btn-save" onClick={save} disabled={saving}>
            {saving ? 'Guardando...' : esEdicion ? 'Actualizar' : 'Establecer'}
          </button>
          <button className="alm-btn-cancel" onClick={onClose}>× Salir</button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal historial de movimientos por producto ─────────────── */

const TH = { padding: '7px 12px', color: '#94a3b8', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', textAlign: 'left' };
const TD = { padding: '7px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

function ModalHistorialProducto({ producto, almacenId, onClose }) {
  const [movs,         setMovs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [ocultarAnul,  setOcultarAnul]  = useState(false);

  useEffect(() => {
    setLoading(true); setError('');
    api.get('/api/movimientos', { params: { almacen_id: almacenId, producto_id: producto.id } })
      .then(r => setMovs(r.data?.data || []))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [almacenId, producto.id]);

  const lista = ocultarAnul ? movs.filter(m => m.estado !== 'ANULADO') : movs;

  return (
    <div className="alm-modal-overlay">
      <div className="alm-modal" style={{ width: 860, maxWidth: '96vw' }}>
        <div className="alm-modal-header">
          <span>
            <FiList size={14} style={{ marginRight: 7 }} />
            Historial — <strong>{producto.nombre}</strong>
          </span>
          <button className="alm-modal-close" onClick={onClose}><FiX /></button>
        </div>

        <div style={{ padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#64748b', display: 'flex', gap: 16, alignItems: 'center' }}>
          <span>Código: <strong>{producto.codigo || '—'}</strong></span>
          <span>Stock actual: <strong>{fmtCant(producto.stock)}</strong> {producto.unidad_minima_short || ''}</span>
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', color: '#475569', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={ocultarAnul}
              onChange={e => setOcultarAnul(e.target.checked)}
              style={{ accentColor: '#1e293b', width: 14, height: 14 }}
            />
            Ocultar anulados
          </label>
        </div>

        <div style={{ maxHeight: 440, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Cargando...</div>
          ) : error ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>{error}</div>
          ) : lista.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Sin movimientos registrados</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#1e293b' }}>
                  <th style={TH}>Fecha</th>
                  <th style={TH}>Número</th>
                  <th style={{ ...TH, textAlign: 'center' }}>Tipo</th>
                  <th style={{ ...TH, textAlign: 'center' }}>Unidad</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Cantidad</th>
                  <th style={TH}>Causa</th>
                  <th style={TH}>Observación</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((m, i) => {
                  const anulado   = m.estado === 'ANULADO';
                  const esEntrada = m.tipo === 'ENTRADA';
                  return (
                    <tr key={m.id} style={{
                      background: i % 2 === 0 ? '#fff' : '#f8fafc',
                      opacity: anulado ? 0.5 : 1,
                      textDecoration: anulado ? 'line-through' : 'none',
                      textDecorationColor: '#ef4444',
                    }}>
                      <td style={{ ...TD, whiteSpace: 'nowrap', color: '#475569' }}>{fmtFecha(m.fecha)}</td>
                      <td style={{ ...TD, fontFamily: 'monospace', fontWeight: 600 }}>{m.numero}</td>
                      <td style={{ ...TD, textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 8, fontWeight: 700, fontSize: 11,
                          background: esEntrada ? '#dcfce7' : '#fef3c7',
                          color: esEntrada ? '#166534' : '#92400e',
                        }}>
                          {esEntrada ? <FiArrowDownCircle size={10}/> : <FiArrowUpCircle size={10}/>}
                          {m.tipo}
                        </span>
                      </td>
                      <td style={{ ...TD, textAlign: 'center' }}>
                        {m.unidad_producto
                          ? <span style={{ display:'inline-block', padding:'1px 6px', background:'#f1f5f9', borderRadius:6, fontSize:11, fontWeight:700, color:'#475569' }}>{m.unidad_producto}</span>
                          : <span style={{ color:'#d1d5db' }}>—</span>}
                      </td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: esEntrada ? '#166534' : '#b45309' }}>
                        {m.cantidad_producto != null
                          ? `${esEntrada ? '+' : '-'}${fmtCant(m.cantidad_producto)}`
                          : '—'}
                      </td>
                      <td style={{ ...TD, color: '#64748b' }}>{m.causa_nombre || '—'}</td>
                      <td style={{ ...TD, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.observacion || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="alm-modal-footer">
          <button className="alm-btn-cancel" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Página principal ────────────────────────────────────────── */
export default function AlmacenesPage() {
  const [almacenes,    setAlmacenes]    = useState([]);
  const [selAlmacen,   setSelAlmacen]   = useState(null);
  const [productos,    setProductos]    = useState([]);
  const [loadingAlm,   setLoadingAlm]   = useState(true);
  const [loadingProd,  setLoadingProd]  = useState(false);
  const [errAlm,       setErrAlm]       = useState('');
  const [errProd,      setErrProd]      = useState('');
  const [search,       setSearch]       = useState('');
  const [modalAlm,     setModalAlm]     = useState(null);
  const [modalAjuste,   setModalAjuste]   = useState(null);
  const [modalHistorial, setModalHistorial] = useState(null); // { producto, almacen_id }
  const [soloSinStock,  setSoloSinStock]  = useState(false);
  const [soloConStock,  setSoloConStock]  = useState(false);
  const [soloNegativo,  setSoloNegativo]  = useState(false);

  /* Cargar almacenes */
  const loadAlmacenes = useCallback(async () => {
    setLoadingAlm(true); setErrAlm('');
    try {
      const r = await api.get('/api/almacenes');
      const list = r.data?.data || [];
      setAlmacenes(list);
      setSelAlmacen(prev => prev || list[0] || null);
    } catch (e) { setErrAlm(e.response?.data?.error || e.message); }
    finally { setLoadingAlm(false); }
  }, []);

  useEffect(() => { loadAlmacenes(); }, [loadAlmacenes]);

  /* Cargar productos del almacén seleccionado */
  const loadProductos = useCallback(async (almacenId, q = '') => {
    setLoadingProd(true); setErrProd('');
    try {
      const r = await api.get(`/api/almacenes/${almacenId}/productos`, { params: { q } });
      setProductos(r.data?.data || []);
    } catch (e) { setErrProd(e.response?.data?.error || e.message); }
    finally { setLoadingProd(false); }
  }, []);

  useEffect(() => {
    if (selAlmacen) { setSoloSinStock(false); setSoloConStock(false); setSoloNegativo(false); loadProductos(selAlmacen.id, search); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selAlmacen, loadProductos]); // search excluido: los cambios de búsqueda los maneja el debounce en handleSearch

  /* Búsqueda con debounce */
  const searchTimer = useRef(null);
  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (selAlmacen) loadProductos(selAlmacen.id, val);
    }, 300);
  };

  const handleToggleAlm = async (a) => {
    try {
      await api.patch(`/api/almacenes/${a.id}/activo`);
      await loadAlmacenes();
    } catch (e) { showToast(e.response?.data?.error || 'Error'); }
  };

  const handleAjusteSaved = (productoId, nuevoStock) => {
    setProductos(prev =>
      prev.map(p => p.id === productoId ? { ...p, stock: nuevoStock } : p)
    );
    setModalAjuste(null);
    showToast('Stock inicial actualizado', 'success');
  };

  /* Estadísticas del almacén seleccionado */
  const totalProductos    = productos.length;
  const conStock          = productos.filter(p => parseFloat(p.stock) > 0).length;
  const sinStockCount     = productos.filter(p => parseFloat(p.stock) === 0).length;
  const negativoCount     = productos.filter(p => parseFloat(p.stock) < 0).length;
  const bajoMinimo        = productos.filter(p => parseFloat(p.stock) < parseFloat(p.stock_minimo) && parseFloat(p.stock_minimo) > 0).length;
  const productosFiltrados = productos.filter(p => {
    const s = parseFloat(p.stock);
    if (soloSinStock && s !== 0) return false;
    if (soloConStock && s <= 0)  return false;
    if (soloNegativo && s >= 0)  return false;
    return true;
  });

  return (
    <div className="alm-page">
      <div className="alm-body">

      {/* ── Panel izquierdo: lista de almacenes ────────────────── */}
      <aside className="alm-sidebar">
        <div className="alm-sidebar-header">
          <span className="alm-sidebar-title"><FiArchive /> Almacenes</span>
          <button className="alm-btn-nuevo-sm" onClick={() => setModalAlm({ mode: 'new' })} title="Nuevo Almacén">
            <FiPlus size={12} /> Nuevo
          </button>
        </div>

        {loadingAlm ? (
          <div className="alm-sidebar-loading">Cargando...</div>
        ) : errAlm ? (
          <div className="alm-sidebar-err">{errAlm}</div>
        ) : (
          <ul className="alm-list">
            {almacenes.map(a => (
              <li
                key={a.id}
                className={`alm-item ${selAlmacen?.id === a.id ? 'selected' : ''} ${!a.activo ? 'inactive' : ''}`}
                onClick={() => setSelAlmacen(a)}
              >
                <div className="alm-item-info">
                  <span className="alm-item-nombre">{a.nombre}</span>
                  {a.descripcion && <span className="alm-item-desc">{a.descripcion}</span>}
                </div>
                <div className="alm-item-actions">
                  <button
                    className="ia-btn ia-btn--edit"
                    onClick={e => { e.stopPropagation(); setModalAlm({ mode: 'edit', data: a }); }}
                    title="Editar"
                  ><FiEdit2 size={15} /></button>
                  <button
                    className={`ia-btn ${a.activo ? 'ia-btn--on' : 'ia-btn--off'}`}
                    onClick={e => { e.stopPropagation(); handleToggleAlm(a); }}
                    title={a.activo ? 'Desactivar' : 'Activar'}
                  >
                    {a.activo ? <FiToggleLeft size={15} /> : <FiToggleRight size={15} />}
                  </button>
                  <FiChevronRight size={11} className="alm-item-arrow" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* ── Panel derecho: productos del almacén ───────────────── */}
      <main className="alm-main">
        {!selAlmacen ? (
          <div className="alm-empty-state">
            <FiArchive size={40} />
            <p>Selecciona un almacén para ver su stock</p>
          </div>
        ) : (
          <>
            {/* Cabecera */}
            <div className="alm-main-header">
              <div className="alm-main-title">
                <FiArchive />
                <span>{selAlmacen.nombre}</span>
                {!selAlmacen.activo && <span className="alm-badge-inactivo">INACTIVO</span>}
              </div>

              {/* KPIs */}
              <div className="alm-kpis">
                <div className="alm-kpi">
                  <span className="alm-kpi-val">{totalProductos}</span>
                  <span className="alm-kpi-lbl">productos</span>
                </div>
                <div className="alm-kpi">
                  <span className="alm-kpi-val alm-kpi-green">{conStock}</span>
                  <span className="alm-kpi-lbl">con stock</span>
                </div>
                {sinStockCount > 0 && (
                  <button
                    className={`alm-kpi alm-kpi-btn ${soloSinStock ? 'alm-kpi-btn-active' : ''}`}
                    onClick={() => setSoloSinStock(v => !v)}
                    title={soloSinStock ? 'Mostrar todos' : 'Ver solo sin stock'}
                  >
                    <span className="alm-kpi-val alm-kpi-gray">{sinStockCount}</span>
                    <span className="alm-kpi-lbl">sin stock{soloSinStock ? ' ×' : ''}</span>
                  </button>
                )}
                {bajoMinimo > 0 && (
                  <div className="alm-kpi alm-kpi-warn">
                    <FiAlertTriangle size={13} />
                    <span className="alm-kpi-val alm-kpi-red">{bajoMinimo}</span>
                    <span className="alm-kpi-lbl">bajo mínimo</span>
                  </div>
                )}
              </div>

              {/* Buscador + filtros */}
              <div className="alm-search-bar">
                <div className="alm-search">
                  <FiSearch size={13} />
                  <input
                    placeholder="Buscar producto..."
                    value={search}
                    onChange={e => handleSearch(e.target.value)}
                  />
                </div>
                <label className="alm-check-filter">
                  <input
                    type="checkbox"
                    checked={soloConStock}
                    onChange={e => { setSoloConStock(e.target.checked); if (e.target.checked) { setSoloSinStock(false); setSoloNegativo(false); } }}
                  />
                  Solo con stock
                </label>
                <label className="alm-check-filter alm-check-neg">
                  <input
                    type="checkbox"
                    checked={soloNegativo}
                    onChange={e => { setSoloNegativo(e.target.checked); if (e.target.checked) { setSoloSinStock(false); setSoloConStock(false); } }}
                  />
                  Stock negativo{negativoCount > 0 && <span className="alm-check-count">{negativoCount}</span>}
                </label>
              </div>
            </div>

            {/* Tabla de productos */}
            {loadingProd ? (
              <div className="alm-prod-loading">Cargando productos...</div>
            ) : errProd ? (
              <div className="alm-prod-err">{errProd}</div>
            ) : (
              <div className="alm-table-wrap">
                <table className="alm-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Producto</th>
                      <th>Familia</th>
                      <th>Und. Compra</th>
                      <th>Und. Mín.</th>
                      <th className="alm-th-stock">Stock</th>
                      <th>Mín.</th>
                      <th className="alm-th-action"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {productosFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="alm-td-empty">
                          <FiPackage size={22} />
                          <span>{
                            soloSinStock  ? 'Todos los productos tienen stock' :
                            soloConStock  ? 'Ningún producto tiene stock' :
                            soloNegativo  ? 'Ningún producto tiene stock negativo' :
                            'Sin productos registrados'
                          }</span>
                        </td>
                      </tr>
                    ) : productosFiltrados.map((p, i) => {
                      const stock    = parseFloat(p.stock);
                      const minimo   = parseFloat(p.stock_minimo);
                      const bajoMin  = minimo > 0 && stock < minimo;
                      const sinStock = stock === 0;
                      return (
                        <tr
                          key={p.id}
                          className={`${i % 2 === 0 ? 'alm-row-even' : 'alm-row-odd'} ${bajoMin ? 'alm-row-warn' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setModalHistorial({ producto: p, almacen_id: selAlmacen.id })}
                        >
                          <td className="alm-td alm-td-cod">{p.codigo || '—'}</td>
                          <td className="alm-td">
                            <span className="alm-prod-nombre">{p.nombre}</span>
                            {p.marca_nombre && (
                              <span className="alm-prod-marca">{p.marca_nombre}</span>
                            )}
                          </td>
                          <td className="alm-td alm-td-familia">{p.familia_nombre || '—'}</td>
                          <td className="alm-td alm-td-unidad alm-td-und-c">
                            {p.unidad_compra_short || p.unidad_compra_nombre || '—'}
                          </td>
                          <td className="alm-td alm-td-unidad">
                            {p.unidad_minima_short || p.unidad_minima_nombre || '—'}
                          </td>
                          <td className="alm-td alm-td-stock">
                            <span className={`alm-stock-badge ${sinStock ? 'alm-stock-zero' : bajoMin ? 'alm-stock-low' : 'alm-stock-ok'}`}>
                              {bajoMin && <FiAlertTriangle size={10} />}
                              {fmtStock(p.stock)}
                            </span>
                          </td>
                          <td className="alm-td alm-td-minimo">
                            {minimo > 0 ? fmtStock(p.stock_minimo) : '—'}
                          </td>
                          <td className="alm-td alm-td-action" onClick={e => e.stopPropagation()}>
                            {p.tiene_movimientos ? (
                              <button
                                className="alm-btn-ajuste"
                                onClick={() => setModalHistorial({ producto: p, almacen_id: selAlmacen.id })}
                                title="Ver historial de movimientos"
                              >
                                <FiList size={12} /> Historial
                              </button>
                            ) : (
                              <button
                                className="alm-btn-ajuste"
                                onClick={() => setModalAjuste(p)}
                                title="Ajuste inicial de stock"
                              >
                                <FiEdit3 size={12} /> Ajustar
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      </div>{/* end alm-body */}

      <div className="alm-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* ── Modales ─────────────────────────────────────────────── */}
      <ModalAlmacen
        modal={modalAlm}
        onClose={() => setModalAlm(null)}
        onSaved={async () => { await loadAlmacenes(); setModalAlm(null); }}
      />

      <ModalAjuste
        item={modalAjuste}
        almacenId={selAlmacen?.id}
        onClose={() => setModalAjuste(null)}
        onSaved={handleAjusteSaved}
      />

      {modalHistorial && (
        <ModalHistorialProducto
          producto={modalHistorial.producto}
          almacenId={modalHistorial.almacen_id}
          onClose={() => setModalHistorial(null)}
        />
      )}
    </div>
  );
}
